import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  platformBackups,
  type BackupGoogleStatus,
  type BackupGoogleUpdate,
  type BackupItem,
  type BackupJob,
  type BackupListing,
  type BackupRequestKind,
} from '@/shared/api/resources';
import { ErrorCode } from '@/shared/api/types';
import { formatBytes, formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import {
  explicarVariavel,
  mensagemDoGoogle,
  origensDeBackup,
  pedidosDeBackup,
  resultadoDoGoogle,
  situacoesNoDrive,
  tiposDeBackup,
  type ResultadoDoGoogle,
} from './backupLabels';

/**
 * Backups da plataforma.
 *
 * Os arquivos ficam na pasta /backup do servidor. O agendamento do servidor
 * continua gravando os dele; esta tela mostra todos, faz um backup na hora, baixa e
 * envia ao Google Drive. Nada aqui apaga backup: apagar e irreversivel e fica para
 * outra versao.
 *
 * Fazer backup e enviar ao Drive respondem antes de terminar. Enquanto algo corre, a
 * tela pergunta de novo a cada poucos segundos, e para de perguntar quando acaba.
 */

const intervaloDeAcompanhamento = 3000;

const chaveDaLista = ['platform', 'backups'] as const;
const chaveDoGoogle = ['platform', 'backups', 'google'] as const;

export function PlatformBackupsPage() {
  const lista = useQuery({
    queryKey: chaveDaLista,
    queryFn: ({ signal }) => platformBackups.list(signal),
    retry: false,
    refetchInterval: (consulta) =>
      emAndamento(consulta.state.data) ? intervaloDeAcompanhamento : false,
  });

  const cabecalho = (
    <PageHeader
      title="Backups"
      description="Copias do banco de dados e dos arquivos enviados, guardadas no servidor. O agendamento do servidor continua fazendo as copias diarias."
    />
  );

  if (lista.isPending) {
    return (
      <div className="flex flex-col gap-5">
        {cabecalho}
        <p className="text-sm text-ink-500">Carregando os backups.</p>
      </div>
    );
  }

  if (lista.isError) {
    return (
      <div className="flex flex-col gap-5">
        {cabecalho}
        <Indisponivel erro={lista.error} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {cabecalho}
      <RetornoDoGoogle />
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <FazerBackup listagem={lista.data} />
        </div>
        <div className="lg:col-span-3">
          <CartaoDoGoogle />
        </div>
      </div>
      <ListaDeBackups listagem={lista.data} />
    </div>
  );
}

function emAndamento(listagem: BackupListing | undefined): boolean {
  if (!listagem) return false;
  return (
    listagem.running !== null ||
    listagem.items.some((item) => item.drive.status === 'enviando')
  );
}

// --- Funcionalidade desligada ---------------------------------------------------

function Indisponivel({ erro }: { erro: Error }) {
  const codigo = erro instanceof ApiError ? erro.code : '';

  if (codigo === ErrorCode.BackupDisabled) {
    return (
      <Card>
        <CardBody className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-ink-900">
            Os backups estao desligados neste servidor
          </h3>
          <p className="text-sm text-ink-600">
            Para ligar, quem cuida do servidor precisa montar a pasta de backups no
            container do backend e informar onde ela esta, na variavel{' '}
            <code className="font-mono text-xs">SEGURACORE_BACKUP_DIR</code> (por
            exemplo <code className="font-mono text-xs">/backup</code>).
          </p>
          <p className="text-sm text-ink-600">
            Os backups do agendamento do servidor continuam sendo feitos normalmente; so
            nao aparecem aqui. O passo a passo esta em docs/DEPLOYMENT.md, na secao de
            backups.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (codigo === ErrorCode.BackupDirMissing) {
    return (
      <Alert tone="warning" title="A pasta de backups nao foi encontrada">
        O servidor esta configurado para usar uma pasta de backups, mas ela nao existe
        dentro do container. Quase sempre e o volume que nao foi montado. Quem cuida do
        servidor deve conferir a montagem de /backup.
      </Alert>
    );
  }

  return (
    <Alert tone="danger" title="Nao foi possivel carregar os backups">
      {erro.message}
    </Alert>
  );
}

// --- Retorno do Google ------------------------------------------------------------

/**
 * Mostra o resultado da conexao com o Google.
 *
 * Ele chega de dois jeitos: pela mensagem da janela do Google, que abre a parte, ou
 * pelo endereco, quando a pessoa volta para esta tela pelo link da janela. O codigo
 * sai do endereco depois de lido, para nao reaparecer ao recarregar.
 */
function RetornoDoGoogle() {
  const [parametros, setParametros] = useSearchParams();
  const [resultado, setResultado] = useState<ResultadoDoGoogle | null>(() =>
    resultadoDoGoogle(parametros.get('google')),
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (parametros.has('google')) {
      const limpos = new URLSearchParams(parametros);
      limpos.delete('google');
      setParametros(limpos, { replace: true });
    }
  }, [parametros, setParametros]);

  useEffect(() => {
    function receber(evento: MessageEvent) {
      // So a propria aplicacao fala com esta tela. Mensagem de outra origem, ou de
      // outro assunto, e ignorada.
      if (evento.origin !== window.location.origin) return;
      const dado = evento.data as { tipo?: unknown; resultado?: unknown } | null;
      if (
        !dado ||
        dado.tipo !== mensagemDoGoogle ||
        typeof dado.resultado !== 'string'
      ) {
        return;
      }
      setResultado(resultadoDoGoogle(dado.resultado));
      void queryClient.invalidateQueries({ queryKey: chaveDoGoogle });
    }
    window.addEventListener('message', receber);
    return () => window.removeEventListener('message', receber);
  }, [queryClient]);

  if (!resultado) return null;
  return (
    <Alert tone={resultado.tom} title={resultado.titulo}>
      {resultado.texto}
    </Alert>
  );
}

// --- Fazer backup agora -----------------------------------------------------------

function FazerBackup({ listagem }: { listagem: BackupListing }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tipo, setTipo] = useState<BackupRequestKind>('banco');

  const opcoes = (Object.keys(pedidosDeBackup) as BackupRequestKind[])
    .filter((pedido) => listagem.files_available || pedido === 'banco')
    .map((pedido) => ({ value: pedido, label: pedidosDeBackup[pedido] }));

  const criar = useMutation({
    mutationFn: (pedido: BackupRequestKind) => platformBackups.create(pedido),
    onSuccess: () => {
      toast.info('Backup comecado. A lista atualiza sozinha quando terminar.');
      void queryClient.invalidateQueries({ queryKey: chaveDaLista });
    },
    onError: (erro) => toast.error(erro.message),
  });

  const correndo = listagem.running;
  const ocupado = criar.isPending || correndo !== null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    criar.mutate(tipo);
  }

  return (
    <Card className="h-full">
      <CardHeader
        title="Fazer backup agora"
        description="Grava uma copia nova na pasta do servidor, alem das do agendamento."
      />
      <CardBody>
        <form className="flex flex-col gap-4" onSubmit={enviar}>
          <Select
            label="O que copiar"
            options={opcoes}
            value={tipo}
            onChange={(evento) => setTipo(evento.target.value as BackupRequestKind)}
            disabled={ocupado}
            help={false}
          />
          {!listagem.files_available ? (
            <p className="text-xs text-ink-500">
              Os arquivos enviados nao ficam no disco deste servidor, entao so o banco
              pode ser copiado aqui.
            </p>
          ) : null}
          <Button type="submit" loading={ocupado}>
            {correndo ? 'Backup em andamento' : 'Fazer backup agora'}
          </Button>
          <SituacaoDoPedido correndo={correndo} ultimo={listagem.last_job} />
        </form>
      </CardBody>
    </Card>
  );
}

function SituacaoDoPedido({
  correndo,
  ultimo,
}: {
  correndo: BackupJob | null;
  ultimo: BackupJob | null;
}) {
  if (correndo) {
    return (
      <Alert tone="info" title="Backup em andamento">
        {pedidosDeBackup[correndo.kind]}, pedido por {correndo.started_by} em{' '}
        {formatDateTime(correndo.started_at)}. Um backup por vez; esta tela avisa quando
        terminar.
      </Alert>
    );
  }
  if (!ultimo) return null;

  const arquivos = ultimo.files.map((arquivo) => arquivo.name).join(', ');
  if (ultimo.status === 'falhou') {
    return (
      <Alert tone="danger" title="O ultimo backup pedido aqui falhou">
        {ultimo.error}
        {arquivos ? ` Ficou pronto: ${arquivos}.` : ''}
      </Alert>
    );
  }
  return (
    <Alert tone="success" title="Ultimo backup pedido aqui concluido">
      {formatDateTime(ultimo.finished_at)}: {arquivos}.
    </Alert>
  );
}

// --- Google Drive -----------------------------------------------------------------

function CartaoDoGoogle() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [aguardando, setAguardando] = useState(false);
  const [bloqueada, setBloqueada] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [mudandoPasta, setMudandoPasta] = useState(false);
  const janela = useRef<Window | null>(null);

  const google = useQuery({
    queryKey: chaveDoGoogle,
    queryFn: ({ signal }) => platformBackups.google(signal),
    retry: false,
    refetchInterval: (consulta) =>
      (aguardando && !consulta.state.data?.connected) || consulta.state.data?.uploading
        ? intervaloDeAcompanhamento
        : false,
  });

  // A espera acaba quando a conta aparece conectada, sem precisar de outro estado.
  const esperando = aguardando && !google.data?.connected;

  // A conexao termina na janela do Google. Quando ela fecha, com ou sem mensagem, a
  // situacao e lida de novo: quem diz se conectou e o servidor.
  useEffect(() => {
    if (!aguardando) return;
    const vigia = window.setInterval(() => {
      if (!janela.current || janela.current.closed) {
        setAguardando(false);
        void queryClient.invalidateQueries({ queryKey: chaveDoGoogle });
      }
    }, 1000);
    return () => window.clearInterval(vigia);
  }, [aguardando, queryClient]);

  const ajustar = useMutation({
    mutationFn: (ajuste: BackupGoogleUpdate) => platformBackups.updateGoogle(ajuste),
    onSuccess: (situacao) => {
      queryClient.setQueryData(chaveDoGoogle, situacao);
      setMudandoPasta(false);
    },
    onError: (erro) => toast.error(erro.message),
  });

  const desconectar = useMutation({
    mutationFn: () => platformBackups.disconnectGoogle(),
    onSuccess: (resultado) => {
      setDesconectando(false);
      if (resultado.revoked_at_google) {
        toast.success(`Conta ${resultado.account_email} desconectada.`);
      } else {
        toast.info(
          `Conta ${resultado.account_email} desconectada aqui, mas o Google nao confirmou a revogacao. Remova o acesso em myaccount.google.com/permissions.`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: chaveDoGoogle });
      void queryClient.invalidateQueries({ queryKey: chaveDaLista });
    },
    onError: (erro) => toast.error(erro.message),
  });

  async function conectar() {
    setBloqueada(false);
    // A janela abre antes da chamada, ainda no clique: aberta depois de esperar a
    // resposta, o navegador a trataria como janela nao pedida e bloquearia.
    //
    // E uma janela, e nao a propria aba, porque a sessao vive na memoria da aba: ir
    // ao Google e voltar recarregaria a aplicacao e pediria o acesso de novo.
    const aberta = window.open('', 'seguracore-google', 'popup,width=520,height=680');
    if (!aberta) {
      setBloqueada(true);
      return;
    }
    janela.current = aberta;
    try {
      const { authorization_url } = await platformBackups.connectGoogle();
      aberta.location.href = authorization_url;
      setAguardando(true);
    } catch (erro) {
      aberta.close();
      toast.error(erro instanceof Error ? erro.message : 'Falha ao conectar.');
    }
  }

  return (
    <Card className="h-full">
      <CardHeader
        title="Google Drive"
        description="Uma copia fora do servidor, numa pasta do seu Google Drive."
      />
      <CardBody className="flex flex-col gap-4">
        {google.isPending ? (
          <p className="text-sm text-ink-500">Carregando a situacao do Google Drive.</p>
        ) : google.isError ? (
          <Alert tone="danger" title="Nao foi possivel ler a situacao do Google Drive">
            {google.error.message}
          </Alert>
        ) : !google.data.configured && !google.data.connected ? (
          <NaoConfigurado situacao={google.data} />
        ) : google.data.connected ? (
          <Conectado
            situacao={google.data}
            onAlternar={(ligado) => ajustar.mutate({ auto_upload: ligado })}
            alternando={ajustar.isPending}
            onMudarPasta={() => setMudandoPasta(true)}
            onDesconectar={() => setDesconectando(true)}
          />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-600">
              Nenhuma conta conectada. Ao conectar, o Google pede para voce escolher a
              conta e autorizar o acesso. A aplicacao so enxerga a pasta que ela mesma
              criar no seu Drive, e nada mais.
            </p>
            <div>
              <Button onClick={() => void conectar()} loading={esperando}>
                {esperando ? 'Aguardando o Google' : 'Conectar conta Google'}
              </Button>
            </div>
            {esperando ? (
              <p className="text-xs text-ink-500">
                Termine a autorizacao na janela do Google. Esta tela atualiza sozinha.
              </p>
            ) : null}
          </div>
        )}

        {bloqueada ? (
          <Alert tone="warning" title="O navegador bloqueou a janela do Google">
            Permita janelas deste site no navegador e clique de novo em Conectar conta
            Google.
          </Alert>
        ) : null}

        <UltimoEnvio situacao={google.data} />
      </CardBody>

      {desconectando && google.data?.connected ? (
        <ConfirmDialog
          open
          onClose={() => setDesconectando(false)}
          onConfirm={() => desconectar.mutate()}
          loading={desconectando && desconectar.isPending}
          tone="danger"
          title="Desconectar a conta Google"
          confirmLabel="Desconectar"
          message={`A conta ${google.data.account_email ?? ''} deixa de receber backups, e o acesso dela e revogado no Google. Os arquivos que ja estao no Drive continuam la.`}
        />
      ) : null}

      {mudandoPasta && google.data?.connected ? (
        <MudarPasta
          atual={google.data.folder_name ?? ''}
          salvando={ajustar.isPending}
          onClose={() => setMudandoPasta(false)}
          onSalvar={(nome) => ajustar.mutate({ folder_name: nome })}
        />
      ) : null}
    </Card>
  );
}

function NaoConfigurado({ situacao }: { situacao: BackupGoogleStatus }) {
  return (
    <div className="flex flex-col gap-3 text-sm text-ink-600">
      <p>
        Este servidor ainda nao esta preparado para o Google Drive. Quem cuida do
        servidor precisa criar uma credencial no Google Cloud Console e informar ao
        servidor:
      </p>
      <ul className="flex flex-col gap-1.5">
        {situacao.missing.map((nome) => (
          <li key={nome} className="flex flex-col">
            <code className="font-mono text-xs text-ink-900">{nome}</code>
            <span className="text-xs text-ink-500">{explicarVariavel(nome)}</span>
          </li>
        ))}
      </ul>
      {situacao.redirect_url ? (
        <p className="text-xs text-ink-500">
          Endereco de retorno para cadastrar no Google:{' '}
          <code className="break-all font-mono text-ink-800">
            {situacao.redirect_url}
          </code>
        </p>
      ) : null}
      <p className="text-xs text-ink-500">
        O passo a passo esta em docs/DEPLOYMENT.md, na secao de backups.
      </p>
    </div>
  );
}

function Conectado({
  situacao,
  onAlternar,
  alternando,
  onMudarPasta,
  onDesconectar,
}: {
  situacao: BackupGoogleStatus;
  onAlternar: (ligado: boolean) => void;
  alternando: boolean;
  onMudarPasta: () => void;
  onDesconectar: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs font-semibold text-ink-500">Conta conectada</dt>
          <dd className="mt-0.5 break-all text-ink-900">{situacao.account_email}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-ink-500">Pasta no Drive</dt>
          <dd className="mt-0.5 flex flex-wrap items-center gap-2 text-ink-900">
            {situacao.folder_url ? (
              <a
                href={situacao.folder_url}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-brand-600 hover:underline"
              >
                {situacao.folder_name}
              </a>
            ) : (
              situacao.folder_name
            )}
            <AcaoComIcone acao="editar" rotulo="Mudar pasta" onClick={onMudarPasta} />
          </dd>
        </div>
      </dl>
      {situacao.connected_at ? (
        <p className="text-2xs text-ink-500">
          Conectada por {situacao.connected_by} em{' '}
          {formatDateTime(situacao.connected_at)}.
        </p>
      ) : null}

      {!situacao.configured ? (
        <Alert
          tone="warning"
          title="Conta conectada, mas o servidor perdeu a configuracao"
        >
          Os envios estao parados ate a configuracao voltar: falta{' '}
          {situacao.missing.join(', ')}.
        </Alert>
      ) : null}

      <label className="flex cursor-pointer items-start gap-3 rounded-md border border-line px-4 py-3">
        <input
          type="checkbox"
          role="switch"
          className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
          checked={situacao.auto_upload}
          disabled={alternando}
          onChange={(evento) => onAlternar(evento.target.checked)}
        />
        <span>
          <span className="block text-sm font-medium text-ink-900">
            Enviar automaticamente
          </span>
          <span className="block text-xs text-ink-500">
            A cada meia hora, e logo depois de cada backup feito aqui, os backups que
            ainda nao estao no Drive sao enviados.
          </span>
        </span>
      </label>

      <div>
        {/* Sozinho no fim do quadro, o desligar da conta fica com o texto ao lado. */}
        <AcaoComIcone
          icone="plug"
          tom="danger"
          rotulo="Desconectar"
          texto
          onClick={onDesconectar}
        />
      </div>
    </div>
  );
}

function UltimoEnvio({ situacao }: { situacao: BackupGoogleStatus | undefined }) {
  if (!situacao) return null;
  if (situacao.uploading) {
    return (
      <p className="text-xs text-info-700">
        Enviando {situacao.uploading.name || 'os backups pendentes'} desde{' '}
        {formatDateTime(situacao.uploading.started_at)}.
      </p>
    );
  }
  const ultimo = situacao.last_upload;
  if (!ultimo) return null;
  if (ultimo.status === 'falhou') {
    return (
      <p className="text-xs text-danger-700">
        O ultimo envio falhou ({ultimo.name}, {formatDateTime(ultimo.finished_at)}):{' '}
        {ultimo.error}
      </p>
    );
  }
  return (
    <p className="text-xs text-ink-500">
      Ultimo envio: {ultimo.name}, em {formatDateTime(ultimo.finished_at)}.
    </p>
  );
}

function MudarPasta({
  atual,
  salvando,
  onClose,
  onSalvar,
}: {
  atual: string;
  salvando: boolean;
  onClose: () => void;
  onSalvar: (nome: string) => void;
}) {
  const [nome, setNome] = useState(atual);
  const limpo = nome.trim();

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Pasta no Google Drive"
      description="Os proximos envios vao para a pasta com este nome. Se ela nao existir, e criada."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={() => onSalvar(limpo)}
            loading={salvando}
            disabled={limpo === '' || limpo === atual}
          >
            Salvar
          </Button>
        </>
      }
    >
      <Field
        label="Nome da pasta"
        value={nome}
        maxLength={100}
        onChange={(evento) => setNome(evento.target.value)}
        help={false}
      />
    </Modal>
  );
}

// --- Lista ------------------------------------------------------------------------

function ListaDeBackups({ listagem }: { listagem: BackupListing }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [baixando, setBaixando] = useState<string | null>(null);

  const google = useQuery({
    queryKey: chaveDoGoogle,
    queryFn: ({ signal }) => platformBackups.google(signal),
    retry: false,
  });
  const podeEnviar = Boolean(google.data?.connected && google.data.configured);
  const enviando = Boolean(google.data?.uploading);

  const enviar = useMutation({
    mutationFn: (nome: string) => platformBackups.sendToDrive(nome),
    onSuccess: (envio) => {
      toast.info(`Envio de ${envio.name} comecado.`);
      void queryClient.invalidateQueries({ queryKey: chaveDaLista });
      void queryClient.invalidateQueries({ queryKey: chaveDoGoogle });
    },
    onError: (erro) => toast.error(erro.message),
  });

  async function baixar(item: BackupItem) {
    setBaixando(item.name);
    try {
      const conteudo = await platformBackups.download(item.name);
      const endereco = URL.createObjectURL(conteudo);
      const ancora = document.createElement('a');
      ancora.href = endereco;
      ancora.download = item.name;
      document.body.append(ancora);
      ancora.click();
      ancora.remove();
      // O endereco fica valido por um minuto: um arquivo grande ainda esta sendo
      // salvo depois do clique, e liberar na hora cortaria o download em alguns
      // navegadores.
      window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000);
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : 'Falha ao baixar.');
    } finally {
      setBaixando(null);
    }
  }

  const colunas: Column<BackupItem>[] = [
    {
      key: 'nome',
      header: 'Nome',
      render: (item) => (
        <span className="font-mono text-xs text-ink-900">{item.name}</span>
      ),
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (item) => tiposDeBackup[item.kind],
    },
    {
      key: 'origem',
      header: 'Origem',
      hideBelow: 'md',
      render: (item) => (
        <Badge tone={item.origin === 'manual' ? 'brand' : 'neutral'}>
          {origensDeBackup[item.origin]}
        </Badge>
      ),
    },
    {
      key: 'tamanho',
      header: 'Tamanho',
      align: 'right',
      render: (item) => (
        <span className="tabular-nums">{formatBytes(item.size_bytes)}</span>
      ),
    },
    {
      key: 'data',
      header: 'Data',
      hideBelow: 'sm',
      render: (item) => (
        <span className="tabular-nums">{formatDateTime(item.created_at)}</span>
      ),
    },
    {
      key: 'drive',
      header: 'Google Drive',
      render: (item) => <SituacaoNoDrive item={item} />,
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (item) => (
        <div className="flex justify-end gap-1">
          <AcaoComIcone
            acao="baixar"
            rotulo="Baixar"
            aria-label={`Baixar ${item.name}`}
            loading={baixando === item.name}
            onClick={() => void baixar(item)}
          />
          <AcaoComIcone
            acao={item.drive.status === 'enviado' ? 'reenviar' : 'enviar'}
            rotulo={
              item.drive.status === 'enviado'
                ? 'Enviar de novo'
                : 'Enviar ao Google Drive'
            }
            aria-label={`Enviar ${item.name} ao Google Drive`}
            motivo={
              podeEnviar
                ? undefined
                : 'Conecte uma conta Google para enviar backups ao Drive.'
            }
            disabled={!podeEnviar || enviando || item.drive.status === 'enviando'}
            loading={enviar.isPending && enviar.variables === item.name}
            onClick={() => enviar.mutate(item.name)}
          />
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Backups no servidor"
        description="Do mais recente para o mais antigo. Banco em /backup/postgres, arquivos em /backup/arquivos."
      />
      <DataTable
        columns={colunas}
        items={listagem.items}
        keyOf={(item) => item.name}
        empty={
          <EmptyState
            icon="layers"
            title="Nenhum backup na pasta"
            description="O agendamento do servidor grava uma copia por dia. Voce tambem pode fazer uma agora."
          />
        }
      />
    </Card>
  );
}

function SituacaoNoDrive({ item }: { item: BackupItem }) {
  const situacao = situacoesNoDrive[item.drive.status];
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span>
        <Badge tone={situacao.tom}>{situacao.rotulo}</Badge>
      </span>
      {item.drive.uploaded_at ? (
        <span className="text-2xs text-ink-500">
          {item.drive.url ? (
            <a
              href={item.drive.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-brand-600 hover:underline"
            >
              Enviado em {formatDateTime(item.drive.uploaded_at)}
            </a>
          ) : (
            `Enviado em ${formatDateTime(item.drive.uploaded_at)}`
          )}
        </span>
      ) : null}
      {item.drive.status === 'falhou' && item.drive.error ? (
        <span className="max-w-64 text-2xs text-danger-700">{item.drive.error}</span>
      ) : null}
    </div>
  );
}
